import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import DrawIt from '../components/slides/activity/DrawIt';
import type { Slide } from '../../utils/supabase/client';

export default function DemoDrawIt() {
  const navigate = useNavigate();
  const [key, setKey] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedDrawing, setSubmittedDrawing] = useState<string | null>(null);

  // Demo slide data
  const demoSlide: Slide = {
    id: 'demo-draw-it',
    lesson_id: 'demo',
    type: 'draw_it',
    slide_order: 0,
    created_at: new Date().toISOString(),
    content: {
      instructions: 'Draw your favorite animal or plant from nature!',
      backgroundColor: '#ffffff',
    },
  };

  const handleSubmit = (answer: any) => {
    setHasSubmitted(true);
    setSubmittedDrawing(answer.drawing);
    console.log('Drawing submitted');
  };

  const handleAnswerChange = (_answer: any) => {
    // Drawing is tracked internally by the component
  };

  const handleRestart = () => {
    setHasSubmitted(false);
    setSubmittedDrawing(null);
    setKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-background to-pink-50 dark:from-purple-950/20 dark:via-background dark:to-pink-950/20">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/demos')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Demos
            </Button>
            {hasSubmitted && (
              <Button variant="outline" onClick={handleRestart}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Draw Again
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Draw-It Demo</h1>
          <p className="text-muted-foreground">
            Express yourself with our canvas drawing tool. Pick colors, adjust brush size, and create!
          </p>
        </div>

        <DrawIt
          key={key}
          slide={demoSlide}
          onSubmit={handleSubmit}
          onAnswerChange={handleAnswerChange}
          selectedAnswer={null}
          hasSubmitted={hasSubmitted}
        />

        {/* Show submitted drawing preview */}
        {hasSubmitted && submittedDrawing && (
          <div className="mt-8 text-center">
            <h3 className="text-lg font-semibold mb-4">Your Submitted Drawing</h3>
            <div className="inline-block bg-card rounded-2xl p-4 border border-border shadow-lg">
              <img
                src={submittedDrawing}
                alt="Your submitted drawing"
                className="max-w-md rounded-xl"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
